import axios from "axios";
import { ICrudOptions } from "@/interfaces/ICrudOptions";
import { isArray, isObject } from "lodash";

/**
 *Basic CRUD Service
 */

export default class CrudService {
  /**
   *
   * @param options
   */
  public async findOne(options: ICrudOptions): Promise<any> {
    let url: string = options.resource;
    if (options.id) {
      url += "/" + String(options.id);
    }
    if (options.extraPath) {
      url += "/" + String(options.extraPath);
    }
    if (options.extraQueryParams) {
      url += "?" + options.extraQueryParams;
    }

    return axios.get(url);
  }

  /**
   *
   * @param options
   */
  public async findAll(options: ICrudOptions): Promise<any> {
    let url = options.resource;
    let queryParams = "";
    if (options.params) {
      if (options.params.sort) {
        let sortQueryParam = "";
        for (const sortParam of options.params.sort) {
          if (sortParam.field && sortParam.type) {
            sortQueryParam +=
              `order[${sortParam.field}]=${sortParam.type}` + "&";
          }
        }
        queryParams += sortQueryParam;
      }
      if (options.params.filter) {
        let filterQueryParam = "";
        for (const filterParam in options.params.filter) {
          if (options.params.filter[filterParam]?.value) {
            filterQueryParam +=
              filterParam +
              "=" +
              options.params.filter[filterParam].value +
              "&";
          }
        }
        queryParams += filterQueryParam;
      }
      if (options.params.page) {
        const pageParam = "page=" + options.params.page + "&";
        queryParams += pageParam;
      }
      if (options.params.itemsPerPage) {
        const perPageParam =
          "itemsPerPage=" + options.params.itemsPerPage + "&";
        queryParams += perPageParam;
      }
      if (options.extraQueryParams) {
        queryParams += "&" + options.extraQueryParams;
      }
      url += "?" + queryParams;
    }
    return axios.get(url);
  }

  /**
   *
   * @param options
   */
  public async findAllSubresource(options: ICrudOptions): Promise<any> {
    let url = `${options.mainResource}/${options.id}/${options.resource}`;
    // TODO code duplication
    let queryParams = "";
    if (options.params) {
      if (options.params.sort) {
        let sortQueryParam = "";
        for (const sortParam of options.params.sort) {
          sortQueryParam += `order[${sortParam.field}]=${sortParam.type}` + "&";
        }
        queryParams += sortQueryParam;
      }
      if (options.params.filter) {
        let filterQueryParam = "";
        for (const filterParam in options.params.filter) {
          filterQueryParam +=
            filterParam + "=" + options.params.filter[filterParam] + "&";
        }
        queryParams += filterQueryParam;
      }
      if (options.params.page) {
        const pageParam = "page=" + options.params.page + "&";
        queryParams += pageParam;
      }
      if (options.params.itemsPerPage) {
        const perPageParam =
          "itemsPerPage=" + options.params.itemsPerPage + "&";
        queryParams += perPageParam;
      }

      if (options.extraQueryParams) {
        queryParams += "&" + options.extraQueryParams;
      }
      url += "?" + queryParams;
    }
    return axios.get(url);
  }

  /**
   *
   * @param options
   */
  public async create(options: ICrudOptions): Promise<any> {
    const data = this.sanitize(options.data);
    return axios.post(options.resource, data);
  }

  /**
   *
   * @param options
   */
  public async upload(options: ICrudOptions): Promise<any> {
    const formData = new FormData();
    for (const prop in options.data) {
      const val = options.data[prop];
      if (isArray(val)) {
        if (val.length > 0 && val[0] instanceof File) {
          for (const file of val) {
            formData.append(prop + "[]", file);
          }
          continue;
        }
        formData.append(prop, JSON.stringify(val));
      } else if (isObject(val) && !(val instanceof File)) {
        formData.append(prop, JSON.stringify(val));
      } else if (val === "" || val === undefined) {
        formData.append(prop, "null");
      } else {
        formData.append(prop, val);
      }
    }
    formData.append("_method", "PUT");
    const headers = { "Content-Type": "multipart/form-data" };
    const url = options.data.id
      ? `${options.resource}/${options.data.id}?_method=PUT`
      : `${options.resource}?_method=PUT`;
    return axios.post(url, formData, { headers });
  }

  public async download(options: ICrudOptions) {
    const cfg: any = {
      responseType: "blob",
    };

    return new Promise((resolve, reject) => {
      axios
        .get(options.resource, cfg)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          resolve({ response, blob_url: url });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public async awsDownload(
    options: ICrudOptions,
    forced = false,
    filename = "download"
  ) {
    const axiosInstance = axios.create();

    const token = axios.defaults.headers.common["Authorization"];
    delete axiosInstance.defaults.headers.common["Authorization"];
    return new Promise((resolve, reject) => {
      axiosInstance
        .get(options.resource, {
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(response.data);

          if (forced) {
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("target", "_blank");
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }

          resolve({ response, blob_url: url });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   *
   * @param options
   */
  public async patch(options: ICrudOptions): Promise<any> {
    return axios.patch(options.resource + "/" + options.id, options.data, {
      headers: {
        "Content-Type": "application/merge-patch+json",
      },
    });
  }

  /**
   *
   * @param options
   */
  public async update(options: ICrudOptions): Promise<any> {
    let url = options.resource;
    url += options.id ? "/" + options.id : "";
    url += options.extraPath ? "/" + options.extraPath : "";

    const data = this.sanitize(options.data);
    return axios.put(url, data);
  }

  /**
   *
   * @param options
   */
  public async delete(options: ICrudOptions): Promise<any> {
    return axios.delete(options.resource + "/" + options.id);
  }

  private sanitize(data: any) {
    const newData: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (value === "") {
        newData[key] = null;
      } else {
        newData[key] = value;
      }
    }
    return newData;
  }
}
